import ModalViewModel from 'infra/ModalViewModel'
import Document from 'documents/Document'
import { action, computed, observable, makeObservable } from 'mobx'
import TabController from 'infra/TabController'
import { ConfirmViewModel } from 'components/ConfirmDialog/ConfirmViewModel'
import { validationContext } from 'validx'
import { Nullable } from 'types/definitions'
import { task } from 'mobx-task'
import { AlertState } from 'alerts/Alert'
import { DocumentDetailsModalPorts } from './ports'
import DocumentType from 'documents/DocumentType'
import isDocumentsApiV5Enabled from 'documents/documentsApiV5FeatureToggle'

export class DocumentDetailsModalViewModel extends ModalViewModel {
  @observable title = ''
  @observable description = ''
  @observable documentType: Nullable<DocumentType> = null

  descriptionLengthLimit = 200

  validation = validationContext(this, Document.validationRules)

  confirmDocumentDelete = new ConfirmViewModel()

  @observable viewAsAdmin = false

  tabController = new TabController<Tabs>(() => [
    {
      tab: Tabs.Details,
      title: 'Details',
      hasError: () => false,
    },
  ])

  constructor(
    public document: Document,
    private adapter: DocumentDetailsModalPorts,
    private _viewAsAdmin?: boolean,
  ) {
    super()
    makeObservable(this)
    this.title = this.document.title
    this.description = this.document.description
    this.documentType = this.document.documentType
    this.viewAsAdmin = this._viewAsAdmin ?? false
  }

  @computed
  get isDetailsTab() {
    return this.tabController.current.tab === Tabs.Details
  }

  @computed
  get hasChanges() {
    return (
      (this.canEditTitleAndDescription &&
        (this.document.title !== this.title ||
          this.document.description !== this.description)) ||
      this.document.documentType !== this.documentType
    )
  }

  /**
   * The v5 API has no endpoint to update the title and description,
   * so those fields are hidden while it's enabled.
   */
  @computed
  get canEditTitleAndDescription() {
    return !isDocumentsApiV5Enabled()
  }

  @computed
  get documentTypes() {
    return [...this.adapter.documentTypes.items]
  }

  @computed
  get canChangedocumentType() {
    return !this.document.type?.id || this.viewAsAdmin
  }

  @computed
  get canEdit() {
    return this.document.canEdit
  }

  @computed
  get headerDescription() {
    if (this.description !== '') {
      if (this.document.description !== this.description) {
        return this.description
      }
      return this.document.description
    }

    return ''
  }

  @computed
  get descriptionCharsRemaining() {
    if (this.description) {
      return this.descriptionLengthLimit - this.description?.length
    }
    return this.descriptionLengthLimit
  }

  @computed
  get headerTitle() {
    if (this.title !== '') {
      if (this.document.title !== this.title) {
        return this.title
      }

      return this.document.title
    }

    return ''
  }

  @computed
  get backingFileName() {
    return this.document.title
  }

  @computed
  get documentWorkspace() {
    return this.document.workspace
  }

  @computed
  get canDownload() {
    return this.document.downloadable
  }

  @computed
  get canRemove() {
    return this.adapter
      .getCurrentMember()
      .hasSomePermissions(
        'DOCS_SELF_REMOVE',
        'DOCS_CLIENT_REMOVE',
        'DOCS_PROVIDER_REMOVE',
      )
  }

  activate = task.resolved(async () => {
    await this.fetchTypes()
    await this.fetchLinkedJobs()
  })

  deactivate() {
    this.close()
  }

  close() {
    this.confirmDocumentDelete.decline()
    this.discardChanges()
    super.close()
  }

  @action
  discardChanges() {
    this.title = this.document.title
    this.description = this.document.description
    this.documentType = this.document.documentType
    this.validation.reset()
  }

  @action.bound
  setViewAsAdmin(viewAsAdmin: boolean) {
    this.viewAsAdmin = viewAsAdmin
  }

  @action.bound
  setDocumentType(type: Nullable<DocumentType>) {
    if (this.canChangedocumentType) {
      this.documentType = type
    }
  }

  @action.bound
  downloadLatest() {
    if (this.canDownload) {
      this.adapter.downloadDocument(this.document)
    }
  }

  fetchTypes = async () => {
    await this.adapter.fetchTypes()
  }

  save = task.resolved(async () => {
    const valid = this.validation.reset().validate().isValid
    if (!valid) {
      return
    }
    try {
      if (
        this.documentType?.shortId &&
        this.canChangedocumentType &&
        this.document.documentType !== this.documentType
      ) {
        await this.adapter.assignType(this.document, {
          documentTypeId: this.documentType.shortId,
        })
      }

      if (this.canEditTitleAndDescription) {
        await this.adapter.updateDocument(this.document, {
          title: this.title,
          description: this.description,
        })
      }

      this.adapter
        .newAlert()
        .withState(AlertState.Success)
        .saying(`Success! Document ${this.document.title} has been updated!`)
        .autoDismiss()
        .show()
      this.close()
    } catch (error) {
      console.error(error)
    }
  })

  delete = task.resolved(async () => {
    if (!(await this.confirmDocumentDelete.show())) {
      return
    }

    try {
      const docTitle = this.document.title
      await this.adapter.archiveDocument(this.document)

      this.adapter
        .newAlert()
        .withState(AlertState.Success)
        .saying(`Success!  Document ${docTitle} has been removed!`)
        .autoDismiss()
        .show()

      this.close()
    } catch (error) {
      this.adapter.newAlert().fromError(error).show()
      console.error(error)
    }
  })

  fetchLinkedJobs = task(
    async () => await this.adapter.fetchLinkedJobs(this.document),
  )
}

export const enum Tabs {
  Details,
}
